import type { GrowthBook } from '@growthbook/growthbook';

import type { ValidCurrency } from '@/types/plans';

import { type Cohort, coerceCohort } from './cohorts';
import { getCohortFromPlanId } from './config';
import { getPricingTestFlagKey } from './flags';

interface ResolveArgs {
  country: string;
  currency: ValidCurrency;
  /** Active subscriber's Stripe plan ID, if known. */
  planId?: number;
}

/**
 * Server-side counterpart of `usePricingCohort`. Reads the per-currency
 * pricing-test flag from the supplied GrowthBook instance (typically obtained
 * via `withGrowthBook` in `getServerSideProps`).
 *
 * Subscribers on a Phase 2 plan get the cohort their plan ID belongs to.
 * Otherwise returns `'control'` when the user's currency has no test (CAD), the flag is
 * unset, or the flag value isn't a recognised cohort (typo defence).
 */
export function resolveCohortSSR(
  growthbook: GrowthBook,
  { currency, country, planId }: ResolveArgs
): { currency: ValidCurrency; cohort: Cohort } {
  // Mirrors `usePricingCohort`: a subscriber's plan ID pins their cohort, and
  // the flag's `subscriptionType = "Free"` targeting no longer matches them.
  const inferredCohort =
    typeof planId === 'number' ? getCohortFromPlanId(planId) : null;
  if (inferredCohort !== null) {
    return { currency, cohort: inferredCohort };
  }

  const flagKey = getPricingTestFlagKey(currency, country);
  if (!flagKey) {
    return { currency, cohort: 'control' };
  }

  const value = growthbook.getFeatureValue<string>(flagKey, 'control');

  return { currency, cohort: coerceCohort(value) };
}
